/**
 * Loan Details Page
 */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { fetchLoanById, approveLoan } from '../loanSlice';
import Button from '../../../components/common/Button/Button';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDate } from '../../../utils/formatDate';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import PERMISSIONS from '../../../constants/permissions';

const LoanDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedLoan, isLoading } = useSelector((state) => state.loans);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    dispatch(fetchLoanById(id));
  }, [dispatch, id]);

  const handleApprove = () => {
    dispatch(approveLoan(id));
  };

  if (isLoading || !selectedLoan) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const canApprove = hasPermission(PERMISSIONS.LOANS_APPROVE);

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button onClick={() => navigate(ROUTES.LOANS.LIST)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Loan Details</h1>
          <p className="text-gray-600 dark:text-gray-400">Loan ID: {selectedLoan.loanId}</p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Loan Information</h3>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Amount:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{formatCurrency(selectedLoan.amount)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Interest Rate:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{selectedLoan.interestRate}%</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Term:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{selectedLoan.termMonths} months</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Status:</dt>
                <dd className="font-medium capitalize text-gray-900 dark:text-white">{selectedLoan.status}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500 dark:text-gray-400">Application Date:</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{formatDate(selectedLoan.createdAt)}</dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Member Information</h3>
            <p className="text-gray-900 dark:text-white font-medium">
              {selectedLoan.member?.firstName} {selectedLoan.member?.lastName}
            </p>
            <p className="text-gray-500 dark:text-gray-400">{selectedLoan.member?.email}</p>
            <p className="text-gray-500 dark:text-gray-400">{selectedLoan.member?.phone}</p>
          </div>
        </div>

        {selectedLoan.status === 'pending' && canApprove && (
          <div className="flex items-center justify-end space-x-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button variant="danger"><XCircle className="w-4 h-4 mr-2" />Reject</Button>
            <Button variant="success" onClick={handleApprove}><CheckCircle className="w-4 h-4 mr-2" />Approve</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanDetails;
